// SUB-OFFICE 7 — FORM 5-S, RETENTION DUTY. A directive of clauses in printed
// precedence decides what the teeth eat and what the Bureau keeps. Feed a doc
// and you must hold it to the teeth for at least 700ms — flinch early and it
// counts as retained AND a fault of nerve. At grade 5 the shredded waste must
// be walked to the DISPOSAL HATCH, which only opens on its cycle. docs5S()
// regenerates the docs + truth; the client records ONLY raw feed/retain/hatch
// events; the server recomputes compliance and prints the after-action reveal.

import { CLAUSES_5S, docs5S, type Doc5S, type Form5SSpec } from '../../shared/physspec';
import { printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

const SEAL_5S = ['REGISTRAR SEAL', 'PROVISIONAL SEAL', 'FIELD SEAL'];
const AGE_5S = ['CURRENT CYCLE', 'SECOND AGE', 'THIRD AGE (OLD CALENDAR)'];
const WMK_5S = ['CHEVRON', 'LATTICE', 'RING'];
const SHRED_MIN_MS = 700;

export function runRetention(
  deps: BenchDeps,
  taskId: string,
  spec: Form5SSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM5S', taskId);
    const bundle = docs5S(spec);
    const layer = printLayer();

    const card = document.createElement('div');
    card.className = 'paper-modal wide';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>FORM 5-S — RETENTION DUTY</span>` +
      `<span class="tab-num" id="rt-count">1 / ${spec.doc_count}</span></div>`;
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;align-items:stretch';
    card.appendChild(wrap);

    const directive = document.createElement('div');
    directive.style.cssText = 'flex:0 0 44%;border-right:1px solid var(--carbon);padding:12px 14px;font-size:10.5px;line-height:1.5';
    directive.innerHTML = '<div class="field-label">STANDING DIRECTIVE — PRINTED PRECEDENCE</div>';
    spec.clause_ids.forEach((id, order) => {
      const r = document.createElement('div');
      r.style.cssText = 'margin:6px 0;padding-left:8px;border-left:2px solid var(--carbon)';
      r.innerHTML = `<b>${order + 1}.</b> ${CLAUSES_5S[id].text}`;
      directive.appendChild(r);
    });
    wrap.appendChild(directive);

    const stage = document.createElement('div');
    stage.className = 'paper-body typewriter';
    stage.style.cssText = 'flex:1;padding:12px 16px';
    wrap.appendChild(stage);

    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    const abandonBtn = document.createElement('button');
    abandonBtn.type = 'button';
    abandonBtn.className = 'slam-btn secondary';
    abandonBtn.textContent = 'ABANDON THE SHIFT';
    foot.appendChild(abandonBtn);
    card.appendChild(foot);
    layer.appendChild(card);

    const countEl = card.querySelector('#rt-count') as HTMLElement;
    let idx = 0;
    let over = false;

    const finishOk = (): void => {
      if (over) return;
      over = true;
      window.removeEventListener('keydown', onKey);
      card.remove();
      const built = rec.build(false);
      if (built.valid) resolve(built.log);
      else {
        deps.bus.emit('toast', `The retention log did not assemble (${built.reason}).`);
        resolve(null);
      }
    };
    const abandon = (): void => {
      if (over) return;
      over = true;
      window.removeEventListener('keydown', onKey);
      card.remove();
      resolve(null);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') abandon(); };
    window.addEventListener('keydown', onKey);
    abandonBtn.addEventListener('click', abandon);

    let directiveShownAt = rec.now();

    const showDoc = (): void => {
      if (over) return;
      if (idx >= spec.doc_count) {
        deps.bus.emit('chyron', 'THE SHIFT ENDS. THE ANNEX WILL RECORD WHAT WAS LOST, IF IT CHOOSES.');
        finishOk();
        return;
      }
      if (idx === 0) rec.ev('directive_dwell', { ms: Math.max(1, rec.now() - directiveShownAt) });
      countEl.textContent = `${idx + 1} / ${spec.doc_count}`;
      const doc: Doc5S = bundle[idx].doc;
      const shownAt = rec.now();
      stage.innerHTML = '';
      const sheet = document.createElement('div');
      sheet.style.cssText = 'border:1px solid var(--carbon);padding:10px 12px;font-size:12px;line-height:1.7;background:var(--paper)';
      sheet.innerHTML =
        `<div class="field-label">DOCUMENT ${idx + 1}</div>` +
        `WATERMARK: ${WMK_5S[doc.watermark]}<br>` +
        `SEAL: ${SEAL_5S[doc.seal]}<br>` +
        `ORIGIN: DEPARTMENT ${doc.dept}<br>` +
        `AGE: ${AGE_5S[doc.age]}<br>` +
        `COPY: ${doc.duplicate ? 'DUPLICATE' : 'ORIGINAL'}` +
        (doc.about_you ? '<br><span style="color:var(--stamp-red)">ADDRESSEE: APPEARS TO BE YOU.</span>' : '');
      stage.appendChild(sheet);

      const terminalDone = (): void => { idx++; window.setTimeout(showDoc, 200); };

      // ---- the teeth (hold ≥700ms) ----------------------------------------
      const teeth = document.createElement('div');
      teeth.style.cssText = 'height:12px;border:1px solid var(--carbon);margin:14px 0 4px;position:relative;overflow:hidden;background:repeating-linear-gradient(90deg,var(--carbon) 0 3px,transparent 3px 7px)';
      const feedFill = document.createElement('div');
      feedFill.style.cssText = 'position:absolute;inset:0 100% 0 0;background:var(--stamp-red);opacity:.5';
      teeth.appendChild(feedFill);
      stage.appendChild(teeth);

      const controls = document.createElement('div');
      controls.style.cssText = 'display:flex;gap:12px;justify-content:center;margin-top:8px';
      const shredBtn = document.createElement('button');
      shredBtn.type = 'button';
      shredBtn.className = 'slam-btn red';
      shredBtn.textContent = 'HOLD TO SHRED';
      shredBtn.style.touchAction = 'none';
      const retainBtn = document.createElement('button');
      retainBtn.type = 'button';
      retainBtn.className = 'slam-btn secondary';
      retainBtn.textContent = 'RETAIN';
      controls.append(shredBtn, retainBtn);
      stage.appendChild(controls);

      let feeding = false;
      let feedStart = 0;
      let raf = 0;
      let settled = false;
      const draw = (): void => {
        if (!feeding) return;
        raf = requestAnimationFrame(draw);
        const held = rec.now() - feedStart;
        feedFill.style.right = `${Math.max(0, 100 - (held / SHRED_MIN_MS) * 100)}%`;
      };
      shredBtn.addEventListener('pointerdown', (e) => {
        if (settled) return;
        e.preventDefault();
        shredBtn.setPointerCapture(e.pointerId);
        feeding = true;
        feedStart = rec.now();
        rec.ev('shred_start', { doc: idx });
        deps.bus.emit('audio:buzz');
        draw();
      });
      const releaseFeed = (): void => {
        if (!feeding || settled) return;
        feeding = false;
        cancelAnimationFrame(raf);
        const held = rec.now() - feedStart;
        if (held >= SHRED_MIN_MS) {
          settled = true;
          rec.ev('shred_complete', { doc: idx, feedMs: Math.round(held) });
          deps.bus.emit('audio:thunk');
          if (spec.waste_egress) { void hatchEgress(terminalDone); }
          else terminalDone();
        } else {
          settled = true;
          rec.ev('shred_aborted', { doc: idx, pct: Math.round((held / SHRED_MIN_MS) * 100) });
          deps.bus.emit('toast', 'You flinched. The document is retained, and the flinch is noted.');
          terminalDone();
        }
      };
      shredBtn.addEventListener('pointerup', releaseFeed);
      shredBtn.addEventListener('pointercancel', releaseFeed);
      retainBtn.addEventListener('click', () => {
        if (settled) return;
        settled = true;
        rec.ev('retained', { doc: idx });
        deps.bus.emit('audio:tick');
        terminalDone();
      });
      void shownAt; // dwell captured on the terminal event below
      // doc_read dwell: emit when a terminal action resolves
      const emitRead = (): void => rec.ev('doc_read', { doc: idx, dwellMs: Math.max(1, rec.now() - shownAt) });
      shredBtn.addEventListener('pointerdown', emitRead, { once: true });
      retainBtn.addEventListener('click', emitRead, { once: true });
    };

    // ---- grade-5 DISPOSAL HATCH (cycles open/closed) ----------------------
    const hatchEgress = (done: () => void): Promise<void> => new Promise((res) => {
      stage.innerHTML = '<div class="field-label">CARRY THE WASTE TO THE DISPOSAL HATCH</div>';
      const ind = document.createElement('div');
      ind.style.cssText = 'text-align:center;font-size:16px;margin:14px 0;letter-spacing:.2em';
      stage.appendChild(ind);
      const deposit = document.createElement('button');
      deposit.type = 'button';
      deposit.className = 'slam-btn';
      deposit.textContent = 'DEPOSIT INTO THE HATCH';
      const wrap2 = document.createElement('div');
      wrap2.style.cssText = 'display:flex;justify-content:center';
      wrap2.appendChild(deposit);
      stage.appendChild(wrap2);
      let raf = 0;
      const tick = (): void => {
        raf = requestAnimationFrame(tick);
        const open = rec.now() % spec.hatch_period_ms < spec.hatch_open_ms;
        ind.textContent = open ? 'HATCH: OPEN' : 'HATCH: SEALED';
        ind.style.color = open ? 'var(--gas)' : 'var(--stamp-red)';
      };
      tick();
      deposit.addEventListener('click', () => {
        const accepted = rec.now() % spec.hatch_period_ms < spec.hatch_open_ms;
        rec.ev('hatch_attempt', { accepted });
        deps.bus.emit(accepted ? 'audio:thunk' : 'audio:buzz');
        if (accepted) {
          cancelAnimationFrame(raf);
          done();
          res();
        } else {
          deps.bus.emit('toast', 'The hatch was sealed. The waste bounces back. Wait for the cycle.');
        }
      });
    });

    deps.bus.emit('chyron', 'RETENTION DUTY. THE DIRECTIVE DECIDES. THE TEETH DO NOT.');
    showDoc();
  });
}
